import {
  useRef,
  useState,
  useEffect,
  useCallback,
  RefObject,
} from 'react';
import {
  getTargetElement,
} from 'web-utils';

import useEventListener from '../useEventListener';

interface Return {
  (el: HTMLElement | null): void;
}
type Refs = RefObject<HTMLElement>[];
type EventType = MouseEvent | TouchEvent;

type Options = {
  refs?: Refs;
  disabled?: boolean;
  eventTypes?: string | string[];
};

const useClickAway = (
  callback: (event: EventType) => void,
  {
    refs: refsOpt,
    disabled = false,
    eventTypes = ['mousedown', 'touchstart'],
  }: Options = {},
) => {
  const callbackRef = useRef(callback);
  const [refsState, setRefsState] = useState<Refs>([]);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const ref: Return = useCallback(el => {
    setRefsState(prevState => [...prevState, { current: el }]);
  }, []);

  useEventListener(
    eventTypes,
    (event: any) => {
      if (!refsOpt?.length && !refsState.length) return;

      const refs = refsOpt || refsState;
      const els: HTMLElement[] = [];
      refs.forEach((item) => {
        const targetElement = getTargetElement(item) as HTMLElement;
        if (targetElement) els.push(targetElement);
      });

      if (!els.length || !els.every(el => !el.contains(event.target))) return;
      callbackRef.current(event);
    },
    {
      target: document,
      disabled,
      passive: true,
      capture: false,
    },
  );

  return ref;
};

export default useClickAway;
